/**
 * What the published figures behind a detail line actually are.
 *
 * A detail line quotes other people's indices -- MHCLG's crime decile, ONS's
 * median price paid, DfT's connectivity metric, Ofsted's grade -- and there is
 * no reason a reader should know what any of them means, or that they are
 * published at all. Each line carries an `i` that says so in a sentence or two
 * and links the publisher's own page.
 *
 * Nothing here explains *this project's* numbers. The percentile in a row, the
 * blend under the sliders and the curves behind them are ours, and a reader
 * chasing a definition of them would land on a government page that has never
 * heard of them. What is described is only what somebody else published, which
 * is also the only part a reader can go and check.
 *
 * `propmap/official.py` is the authority on these pages -- it is the module
 * that fetches them, and it carries the same URLs as constants. A page that
 * moves has to move in both files.
 */

export interface SourceNote {
  /** the published thing, in its publisher's own words */
  title: string;
  publisher: string;
  /** one or two sentences: what the index is, never what we did with it */
  what: string;
  url: string;
}

/**
 * Per category, the indices its detail line quotes, in the order the line
 * quotes them. A category with nothing published behind it has no entry and
 * shows no `i`, rather than an `i` that opens on an apology.
 */
export const SOURCE_NOTES: Record<string, SourceNote[]> = {
  price: [
    {
      title: "Median house prices by MSOA",
      publisher: "ONS, from HM Land Registry",
      what: "ONS publishes the median price paid for homes sold in each neighbourhood of " +
            "roughly 7,500 people, taken from HM Land Registry's record of completed sales.",
      url: "https://www.ons.gov.uk/peoplepopulationandcommunity/housing/datasets/" +
           "medianhousepricesbymiddlelayersuperoutputarea",
    },
  ],
  crime: [
    {
      title: "English Indices of Deprivation 2025, crime domain",
      publisher: "MHCLG",
      what: "MHCLG ranks every neighbourhood in England on the risk of being a victim of " +
            "violence, burglary, theft or criminal damage, and cuts that ranking into " +
            "tenths.",
      url: "https://www.gov.uk/government/statistics/english-indices-of-deprivation-2025",
    },
    {
      title: "MPS recorded crime by LSOA",
      publisher: "Metropolitan Police",
      what: "The offence count is the Met's own tally of crimes it recorded in this " +
            "neighbourhood over twelve months, published monthly through the London " +
            "Datastore. Every category the Met publishes is counted.",
      url: "https://data.london.gov.uk/dataset/recorded_crime_summary",
    },
  ],
  transport: [
    {
      title: "Transport connectivity metric",
      publisher: "Department for Transport",
      what: "DfT scores each neighbourhood out of 100 for what it calls connectivity: " +
            "someone's ability to reach employment, services and social engagements from " +
            "where they live. It is built from timetables and road speeds.",
      url: "https://www.gov.uk/government/publications/transport-connectivity-metric",
    },
  ],
  schools: [
    {
      title: "Ofsted school inspection outcomes",
      publisher: "Ofsted",
      what: "Ofsted inspects state schools and publishes the judgement from each school's " +
            "most recent inspection, along with when that inspection was.",
      url: "https://www.gov.uk/government/statistical-data-sets/" +
           "monthly-management-information-ofsteds-school-inspections-outcomes",
    },
  ],
};

/**
 * The host a note links to, as a reader would name it: "gov.uk", "ons.gov.uk".
 *
 * Read off the URL rather than written beside it, so a link and its label
 * cannot come to disagree about where it goes.
 */
export function linkHost(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "the publisher";
  }
}
